import { supabase } from '../lib/supabase';

export interface CampaignAccess {
  id: string;
  campaign_id: string;
  user_id: string;
  granted_by: string | null;
  created_at: string;
}

export interface UserWithAccess {
  user_id: string;
  email: string;
  has_access: boolean;
}

export const campaignAccessService = {
  async grantAccessToAllUsers(campaignId: string): Promise<void> {
    const { error } = await supabase.rpc('grant_all_users_access_to_campaign', {
      p_campaign_id: campaignId
    });

    if (error) throw error;
  },

  async grantAccess(campaignId: string, userId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('campaign_user_access')
      .insert({
        campaign_id: campaignId,
        user_id: userId,
        granted_by: user.id
      });

    if (error) throw error;
  },

  async revokeAccess(campaignId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('campaign_user_access')
      .delete()
      .eq('campaign_id', campaignId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async getUsersWithAccess(campaignId: string): Promise<UserWithAccess[]> {
    const { data: allUsers, error: usersError } = await supabase
      .rpc('get_users_by_role', { p_role: 'agent' });

    if (usersError) throw usersError;
    if (!allUsers) return [];

    const { data: accessRecords, error: accessError } = await supabase
      .from('campaign_user_access')
      .select('user_id')
      .eq('campaign_id', campaignId);

    if (accessError) throw accessError;

    const userIdsWithAccess = new Set(accessRecords?.map(r => r.user_id) || []);

    return allUsers.map(user => ({
      user_id: user.user_id,
      email: user.email,
      has_access: userIdsWithAccess.has(user.user_id)
    }));
  },

  async getCampaignIdsByUserAccess(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('campaign_user_access')
      .select('campaign_id')
      .eq('user_id', userId);

    if (error) throw error;
    return data?.map(r => r.campaign_id) || [];
  },

  async hasAccess(campaignId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('user_has_campaign_access', {
      p_user_id: userId,
      p_campaign_id: campaignId
    });

    if (error) throw error;
    return data || false;
  },

  async toggleAccess(campaignId: string, userId: string, grantAccess: boolean): Promise<void> {
    if (grantAccess) {
      await this.grantAccess(campaignId, userId);
    } else {
      await this.revokeAccess(campaignId, userId);
    }
  }
};
